import {
  Box,
  Checkbox,
  Chip,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  OutlinedInput,
  Typography,
} from "@mui/material";
import React from "react";

import Editor from "../../utils/common/Editor";
import { FEEDBACK_TYPE_BUGS } from "../../model";
import PreAISettings from "./PreAISettings";
import AISettings from "./AISettings";
import { IConnectionDetails, ITicketAccordionSettings } from "./types";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
} from "../../utils/common/common";
import ConnectionDetails from "./ConnectionDetails";

interface EmailSettingsTicketProps {
  bugSeverities: string[];
  aiSettings: any;
  setAISettings: (newState: any) => void;
  accordionSettings: ITicketAccordionSettings;
  handleAccordionChange: (name: string) => void;
  kbArticles: any[];
  connectionDetails: IConnectionDetails;
  setConnectionDetails: (newState: IConnectionDetails) => void;
  isAutoAssign: any;
  handleIsAutoAssignChange: (event: any, type: string) => void;
  emailSettings: any;
  setEmailSettings: (newState: any) => void;
}

const Ticket = ({
  bugSeverities,
  aiSettings,
  setAISettings,
  accordionSettings,
  handleAccordionChange,
  kbArticles,
  connectionDetails,
  setConnectionDetails,
  isAutoAssign,
  handleIsAutoAssignChange,
  emailSettings,
  setEmailSettings,
}: EmailSettingsTicketProps) => {
  const handleCheckboxChange = (e: any) => {
    const { name: controlName, checked } = e.target;
    setEmailSettings((prevState: any) => ({
      ...prevState,
      [controlName]: checked,
    }));
  };

  const handleInputChange = (e: any) => {
    const { name: controlName, value } = e.target;
    setEmailSettings((prevState: any) => {
      return {
        ...prevState,
        [controlName]: value,
      };
    });
  };

  const handleRTEChange = (value: string, controlName: string) => {
    setEmailSettings((prevState: any) => {
      return {
        ...prevState,
        [controlName]: value,
      };
    });
  };

  const handleChipClick = (severity: string) => {
    setEmailSettings((prevState: any) => {
      let updatedAutoReplySeverities = { ...prevState.autoReplySeverities };

      if (severity === "Any") {
        if (updatedAutoReplySeverities[severity]) {
          delete updatedAutoReplySeverities[severity];
        } else {
          updatedAutoReplySeverities = { "Any": true };
        }
      } else {
        updatedAutoReplySeverities[severity] =
          !updatedAutoReplySeverities[severity];
      }

      return {
        ...prevState,
        autoReplySeverities: updatedAutoReplySeverities,
      };
    });
  };

  const isChipDisabled = (severity: string) => {
    if (emailSettings.autoReplySeverities["Any"] && severity !== "Any") {
      return true;
    }

    return false;
  };

  return (
    <Grid item xs={12}>
      <Box className="feedback-email-wrap tab-content">
        {/* Connection details */}
        <ConnectionDetails
          name="ticketCD"
          accordionSettings={accordionSettings}
          handleAccordionChange={handleAccordionChange}
          connectionDetails={connectionDetails}
          setConnectionDetails={setConnectionDetails}
        />

        {/* Email configuration */}
        <Box className="accordion-wrap" sx={{ mt: "16px" }}>
          <Accordion
            expanded={accordionSettings["emailSettings"]}
            onChange={() => handleAccordionChange("emailSettings")}
          >
            <AccordionSummary
              aria-controls={"panel1-content"}
              id={"panel1-header"}
            >
              <Grid container className="acc-prod-icon">
                <Grid item className="acc-prod-icon">
                  Email Settings
                </Grid>
              </Grid>
            </AccordionSummary>
            <AccordionDetails>
              <Grid item xs={12}>
                <Box sx={{ display: "flex", flexDirection: "column" }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="sendAutoReply"
                        checked={emailSettings.sendAutoReply}
                        onChange={handleCheckboxChange}
                        color="primary"
                      />
                    }
                    label={"Send auto reply email when user reports issue"}
                  />
                </Box>
              </Grid>
              {emailSettings.sendAutoReply ? (
                <Box className="send-auto-reply">
                  <Typography sx={{ mt: 1.5 }} className="label">
                    Choose severities that activate email message - can select
                    multiple
                  </Typography>
                  <Grid item className="ratings-chip" xs={12} sx={{ mt: 2 }}>
                    {bugSeverities.length > 0
                      ? bugSeverities.map((severity: any) => (
                          <Chip
                            key={severity}
                            disabled={isChipDisabled(severity)}
                            onClick={() => handleChipClick(severity)}
                            variant={
                              emailSettings.autoReplySeverities?.[severity]
                                ? "filled"
                                : "outlined"
                            }
                            label={severity}
                            color="primary"
                          />
                        ))
                      : " No severity "}
                  </Grid>
                  <Grid item xs={12} sx={{ mt: 2 }}>
                    <FormControl className="custom-select" fullWidth>
                      <InputLabel>
                        Subject of email
                        <div
                          style={{
                            display: "inline-block",
                            color: "red",
                            marginLeft: "1px",
                          }}
                        >
                          *
                        </div>
                      </InputLabel>
                      <OutlinedInput
                        type="text"
                        name="autoReplyEmailSubject"
                        value={emailSettings.autoReplyEmailSubject}
                        onChange={handleInputChange}
                        id="bug-msg-input"
                        label="bug email subject"
                      />
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sx={{ mt: 2 }}>
                    <FormControl className="custom-select" fullWidth>
                      <InputLabel className="richtext">
                        Body of auto reply email
                        <div
                          style={{
                            display: "inline-block",
                            color: "red",
                            marginLeft: "1px",
                          }}
                        >
                          *
                        </div>
                      </InputLabel>
                      <Editor
                        updateData={(value: string) =>
                          handleRTEChange(value, "autoReplyEmailBody")
                        }
                        editorData={emailSettings.autoReplyEmailBody}
                        id="bug-email-body"
                        label="bug email msg"
                      />
                    </FormControl>
                  </Grid>
                </Box>
              ) : null}
              <>
                <Typography sx={{ mt: 1.5 }} className="label">
                  {`Insert #{{productname}} and #{{productversion}} in the text if you want to put these values in the mail`}
                </Typography>

                <Grid item xs={12} sx={{ mt: 2 }}>
                  <FormControl className="custom-select" fullWidth>
                    <InputLabel className="richtext">
                      Salutation of email
                    </InputLabel>
                    <Editor
                      updateData={(value: string) =>
                        handleRTEChange(value, "emailSalutation")
                      }
                      editorData={emailSettings.emailSalutation}
                      id="bug-email-salutation"
                      label="bug email salutation"
                    />
                  </FormControl>
                </Grid>
                <Grid item xs={12} sx={{ mt: 3 }}>
                  <FormControl className="custom-select" fullWidth>
                    <InputLabel className="richtext">
                      Signature of email
                    </InputLabel>
                    <Editor
                      updateData={(value: string) =>
                        handleRTEChange(value, "emailSignature")
                      }
                      editorData={emailSettings.emailSignature}
                      id="bug-email-signature"
                      label="bug email signature"
                    />
                  </FormControl>
                </Grid>
              </>
            </AccordionDetails>
          </Accordion>
        </Box>

        <PreAISettings
          name="ticketPreAISettings"
          aiSettings={aiSettings}
          setAISettings={setAISettings}
          accordionSettings={accordionSettings}
          handleAccordionChange={handleAccordionChange}
          sx={{ marginTop: "16px" }}
        />

        <AISettings
          name="ticketAISettings"
          aiSettings={aiSettings}
          setAISettings={setAISettings}
          accordionSettings={accordionSettings}
          handleAccordionChange={handleAccordionChange}
          kbArticles={kbArticles}
          sx={{ marginTop: "16px" }}
        />
      </Box>
    </Grid>
  );
};

export default Ticket;
